/**
 * Phase 4c — Galerie photos du devis.
 *
 * - Upload : drag&drop desktop + `<input type="file" accept="image/*" capture="environment">` mobile
 * - Suppression : bouton × sur chaque vignette (confirmation)
 * - HEIC : accepté tel quel, rendu via URL streaming (le navigateur affiche ou propose téléchargement)
 * - Contraintes serveur : PNG/JPG/HEIC ≤ 10 MB, max 20 photos (Phase 4a)
 *
 * Endpoint stream : GET /api/quotes/{id}/photos/{photo_id} → nécessite auth Bearer.
 * On construit l'URL avec le token dans la query string ? Non — le stream nécessite le header.
 * Solution : fetch blob → objectURL. Réalisé au montage pour chaque photo.
 */
import { useEffect, useRef, useState } from "react";
import { api, formatApiError } from "@/lib/api";
import { toast } from "sonner";
import { Card, CardContent } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Loader2, Upload, Camera, Trash2, ImagePlus } from "lucide-react";
import imageCompression from "browser-image-compression";

// Phase 10e-fix — Compression client-side pour photos chantier iPhone (souvent >10 Mo).
//  - Cible max 2 Mo après compression + max 2000px côté long (JPEG qualité ~85 %)
//  - HEIC : le canvas ne peut pas décoder → on skip la compression, le back accepte HEIC tel quel
//  - Garde-fou dur front : 20 Mo max post-compression (défense en profondeur, back valide aussi)
const MAX_MB_HARD = 20;
const MAX_COUNT = 20;
const ALLOWED = ["image/png", "image/jpeg", "image/jpg", "image/heic", "image/heif"];
const COMPRESSIBLE_TYPES = ["image/png", "image/jpeg", "image/jpg"];
const COMPRESSION_OPTS = {
  maxSizeMB: 2,
  maxWidthOrHeight: 2000,
  useWebWorker: true,
  initialQuality: 0.85,
};

function PhotoTile({ quoteId, photo, isDraft, onDeleted }) {
  const [url, setUrl] = useState(null);
  const [broken, setBroken] = useState(false);
  const [deleting, setDeleting] = useState(false);

  useEffect(() => {
    let objUrl = null;
    let cancelled = false;
    (async () => {
      try {
        const res = await api.get(`/quotes/${quoteId}/photos/${photo.id}`, {
          responseType: "blob",
        });
        if (cancelled) return;
        objUrl = URL.createObjectURL(res.data);
        setUrl(objUrl);
      } catch {
        if (!cancelled) setBroken(true);
      }
    })();
    return () => {
      cancelled = true;
      if (objUrl) URL.revokeObjectURL(objUrl);
    };
  }, [quoteId, photo.id]);

  const del = async () => {
    if (!window.confirm(`Supprimer « ${photo.original_name || "cette photo"} » ?`)) return;
    setDeleting(true);
    try {
      const { data } = await api.delete(`/quotes/${quoteId}/photos/${photo.id}`);
      onDeleted(data);
      toast.success("Photo supprimée");
    } catch (e) {
      toast.error(formatApiError(e));
    } finally {
      setDeleting(false);
    }
  };

  const isHeic = /heic|heif/i.test(photo.mime_type || "");

  return (
    <div
      className="group relative aspect-square overflow-hidden rounded border border-slate-800 bg-slate-950/40"
      data-testid={`photo-tile-${photo.id}`}
    >
      {url && !broken ? (
        <img
          src={url}
          alt={photo.original_name || "Photo devis"}
          className="h-full w-full object-cover"
          onError={() => setBroken(true)}
        />
      ) : (
        <div className="flex h-full w-full flex-col items-center justify-center gap-1 p-2 text-center text-[10px] text-slate-400">
          {broken ? (
            <>
              {isHeic ? "HEIC — non prévisualisable" : "Prévisualisation indisponible"}
              <a
                href="#"
                onClick={async (e) => {
                  e.preventDefault();
                  try {
                    const res = await api.get(`/quotes/${quoteId}/photos/${photo.id}`, {
                      responseType: "blob",
                    });
                    const blobUrl = URL.createObjectURL(res.data);
                    window.open(blobUrl, "_blank");
                  } catch {
                    toast.error("Téléchargement impossible");
                  }
                }}
                className="text-slate-500 underline-offset-2 hover:text-slate-200 hover:underline"
              >
                Télécharger
              </a>
            </>
          ) : (
            <Loader2 className="h-4 w-4 animate-spin text-slate-500" />
          )}
        </div>
      )}

      {/* Overlay actions — Phase 2C.M+ P0 (2026-09-06) : icône TOUJOURS
          visible sur tactile (pas de hover iPad). Zone tactile ≥ 44×44 px
          (Apple HIG). Desktop : discret par défaut, plein opacité au hover. */}
      {isDraft && (
        <button
          onClick={del}
          disabled={deleting}
          className="absolute right-1 top-1 inline-flex h-11 w-11 items-center justify-center rounded-full bg-slate-900/85 text-red-400 backdrop-blur-sm transition hover:bg-red-500/25 focus:outline-none focus:ring-2 focus:ring-red-400/50 lg:opacity-70 lg:group-hover:opacity-100"
          data-testid={`photo-delete-${photo.id}`}
          aria-label={`Supprimer la photo ${photo.original_name || ""}`}
        >
          {deleting ? <Loader2 className="h-4 w-4 animate-spin" /> : <Trash2 className="h-4 w-4" />}
        </button>
      )}
      <div className="absolute inset-x-0 bottom-0 truncate bg-slate-950/70 px-1.5 py-0.5 text-[10px] text-slate-300">
        {photo.original_name || "—"}
      </div>
    </div>
  );
}

export default function PhotoGallery({ quote, setQuote, isDraft }) {
  const fileRef = useRef(null);
  const captureRef = useRef(null);
  const [uploading, setUploading] = useState(false);
  const [compressing, setCompressing] = useState(false);
  const [dragOver, setDragOver] = useState(false);

  const photos = quote?.photos || [];
  const remaining = MAX_COUNT - photos.length;
  const canUpload = isDraft && remaining > 0;

  const uploadFile = async (file) => {
    if (!ALLOWED.includes(file.type.toLowerCase())) {
      toast.error(`Format non supporté : ${file.type || "inconnu"}`, {
        description: "Formats acceptés : PNG, JPG, HEIC",
      });
      return;
    }

    // Phase 10e-fix — Compression client-side JPEG/PNG (HEIC ne peut pas être décodé par canvas)
    let toSend = file;
    const isCompressible = COMPRESSIBLE_TYPES.includes(file.type.toLowerCase());
    const originalMb = file.size / 1024 / 1024;
    if (isCompressible && originalMb > 2) {
      // Indicateur "Compression..." après 200 ms seulement (transparent si rapide)
      const spinnerTimer = setTimeout(() => setCompressing(true), 200);
      try {
        const compressed = await imageCompression(file, COMPRESSION_OPTS);
        // Reconstruit un File avec le nom d'origine pour préserver l'extension côté back
        toSend = new File([compressed], file.name, {
          type: compressed.type || "image/jpeg",
          lastModified: file.lastModified,
        });
        const finalMb = toSend.size / 1024 / 1024;
        if (originalMb > 2 && finalMb < originalMb) {
          toast.success(
            `Photo compressée : ${originalMb.toFixed(1)} Mo → ${finalMb.toFixed(1)} Mo`,
            { duration: 2000 },
          );
        }
      } catch (compressErr) {
        // En cas d'échec de compression, tente d'envoyer l'original (le back tranchera)
        console.warn("Compression échouée, envoi de l'original", compressErr);
      } finally {
        clearTimeout(spinnerTimer);
        setCompressing(false);
      }
    }

    // Garde-fou dur : même après compression si toujours > 20 Mo, on refuse
    if (toSend.size > MAX_MB_HARD * 1024 * 1024) {
      toast.error(
        `Photo trop volumineuse (${(toSend.size / 1024 / 1024).toFixed(1)} Mo)`,
        { description: `Réessaie avec une photo plus petite (max ${MAX_MB_HARD} Mo).` },
      );
      return;
    }

    const fd = new FormData();
    fd.append("file", toSend);
    try {
      setUploading(true);
      const { data } = await api.post(`/quotes/${quote.id}/photos`, fd, {
        headers: { "Content-Type": "multipart/form-data" },
      });
      setQuote(data);
    } catch (e) {
      toast.error(formatApiError(e));
    } finally {
      setUploading(false);
    }
  };

  const uploadFiles = async (files) => {
    const arr = Array.from(files);
    for (const f of arr) {
      if (photos.length + (fileRef.current?._pending || 0) >= MAX_COUNT) {
        toast.error(`Limite atteinte : ${MAX_COUNT} photos max/devis`);
        break;
      }
      await uploadFile(f);
    }
  };

  const onDrop = async (e) => {
    e.preventDefault();
    setDragOver(false);
    if (!canUpload) return;
    if (e.dataTransfer?.files?.length) {
      await uploadFiles(e.dataTransfer.files);
    }
  };

  return (
    <Card className="border-slate-800 bg-slate-900/40" data-testid="photo-gallery">
      <CardContent className="space-y-3 p-4">
        <div className="flex items-center justify-between">
          <h3 className="text-xs uppercase tracking-wider text-slate-400">Photos du chantier</h3>
          <span className="text-[10px] tabular text-slate-500" data-testid="photo-count">
            {photos.length} / {MAX_COUNT}
          </span>
        </div>

        {photos.length === 0 && !canUpload && (
          <div className="rounded border border-dashed border-slate-800 p-6 text-center text-xs text-slate-500">
            Aucune photo attachée.
          </div>
        )}

        {photos.length > 0 && (
          <div className="grid grid-cols-3 gap-2 sm:grid-cols-4 md:grid-cols-5">
            {photos.map((p) => (
              <PhotoTile
                key={p.id}
                quoteId={quote.id}
                photo={p}
                isDraft={isDraft}
                onDeleted={setQuote}
              />
            ))}
          </div>
        )}

        {canUpload && (
          <div
            onDragOver={(e) => {
              e.preventDefault();
              setDragOver(true);
            }}
            onDragLeave={() => setDragOver(false)}
            onDrop={onDrop}
            className={`flex flex-col items-center gap-2 rounded border-2 border-dashed p-4 transition ${
              dragOver
                ? "border-[#C4553B] bg-[#C4553B]/5"
                : "border-slate-700 bg-slate-950/40"
            }`}
            data-testid="photo-dropzone"
          >
            <ImagePlus className="h-6 w-6 text-slate-500" />
            <div className="text-center text-[11px] text-slate-400">
              Glisse tes photos ici, ou :
            </div>
            <div className="flex flex-wrap gap-2">
              <input
                ref={fileRef}
                type="file"
                accept="image/png,image/jpeg,image/heic,image/heif"
                multiple
                className="hidden"
                onChange={(e) => e.target.files && uploadFiles(e.target.files)}
                data-testid="photo-file-input"
              />
              <input
                ref={captureRef}
                type="file"
                accept="image/*"
                capture="environment"
                className="hidden"
                onChange={(e) => e.target.files && uploadFile(e.target.files[0])}
                data-testid="photo-capture-input"
              />
              <Button
                size="sm"
                variant="outline"
                onClick={() => fileRef.current?.click()}
                disabled={uploading || compressing}
                className="border-slate-700 bg-slate-800/60 text-slate-200"
                data-testid="photo-select-files-btn"
              >
                {(uploading || compressing) ? <Loader2 className="mr-1 h-3 w-3 animate-spin" /> : <Upload className="mr-1 h-3 w-3" />}
                {compressing ? "Compression…" : uploading ? "Envoi…" : "Sélectionner"}
              </Button>
              <Button
                size="sm"
                variant="outline"
                onClick={() => captureRef.current?.click()}
                disabled={uploading || compressing}
                className="border-slate-700 bg-slate-800/60 text-slate-200 sm:hidden"
                data-testid="photo-capture-btn"
              >
                <Camera className="mr-1 h-3 w-3" /> Prendre
              </Button>
            </div>
            <div className="text-[10px] text-slate-500" data-testid="photo-upload-hint">
              PNG · JPG · HEIC — compression auto pour photos iPhone, {remaining} restante(s)
            </div>
          </div>
        )}
      </CardContent>
    </Card>
  );
}
